import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import React, { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'
import { useIsRTL } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2 } from '@ui/Typography'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import {
  SensitiveTableCell,
  Table,
  TableBody,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'
import { TableHeaderTooltip } from '@ui/v2/Table/TableHeaderTooltip'

import { SortOrder } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'

import { NameDto } from '@model/NameDto'
import { RebateTransactionDto, RebateTransactionSortKeys } from '@model/RebateTransactionDto'
import { TransactionStatus } from '@model/TransactionStatus'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { StatusIcon, TRANSACTION_STATUS_STYLE } from '@shared/components/StatusIcon'

interface TransactionsPageTableProps {
  sortOptions?: NameDto<RebateTransactionSortKeys | null>[]
  sticky?: 'left'
  tableQuery: PaginatedQueryResult<RebateTransactionDto>
  transactionStatuses: Partial<Record<TransactionStatus, string>>
}

export const IBRebatesTransactionsTable: FC<TransactionsPageTableProps> = (props) => {
  const { tableQuery, sortOptions, sticky, transactionStatuses } = props
  const { isLoading, pageQuery, setPageQuery, data } = tableQuery
  const { formatNumber, formatMoney } = useFormatNumber()
  const [auth] = useContext(AuthSessionContext)
  const isRTL = useIsRTL()
  const { t } = useTranslation()
  const dateFormat = auth?.dateFormatType?.name
  const defaultSort = 'transactionDate'
  const defaultSortOrder: SortOrder = 'DESC'
  const getColumnAlign = (id?: RebateTransactionSortKeys) => {
    switch (id) {
      case 'transactionDate':
      case 'tradingAccountName':
      case 'accountType':
      case 'state':
        return isRTL ? 'right' : 'left'
      default:
        return isRTL ? 'left' : 'right'
    }
  }
  return (
    <Table
      isLoading={isLoading}
      noResults={
        !data.length && (
          <EmptyTransactionsPageTable
            isAnyFilterActive={Object.entries(pageQuery?.search ?? {}).some(
              ([key, value]) => key !== 'ca_search_TextSearch' && !!value
            )}
          />
        )
      }
      sticky={sticky}
      hideScrollbarOnMobile
    >
      <TableHeader>
        <TableRow>
          {sortOptions?.map((option) =>
            option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                sort={pageQuery?.sort || defaultSort}
                sortOrder={pageQuery?.sortOrder ?? defaultSortOrder}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({
                    ...pageQuery,
                    sort,
                    sortOrder,
                  })
                }}
                align={getColumnAlign(option.id)}
              >
                {option.name}
                <TableHeaderTooltip<Partial<Record<RebateTransactionSortKeys, string>>>
                  headerIds={{
                    accumulatedVolumeFxm: t('IB.RebatesPage.Forex & Metals'),
                    accumulatedVolumeOther: t('IB.RebatesPage.Other instrument groups'),
                    accumulatedCommission: t('IB.RebatesPage.Commission accrued for TA'),
                  }}
                  targetHeaderId={option.id}
                />
              </TableHeadSort>
            ) : (
              <TableHead key={option.name}>{option.name}</TableHead>
            )
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((transaction) => (
          <TableRow key={transaction.id}>
            <SensitiveTableCell width={157} alert={transaction.showNotification}>
              {formatDate(transaction.date, { formatType: dateFormat, withTime: false })}
            </SensitiveTableCell>
            <SensitiveTableCell width={157}>{transaction.tradingAccount?.name}</SensitiveTableCell>
            <SensitiveTableCell width={157}>
              {transaction.tradingAccountType?.name}
            </SensitiveTableCell>
            <SensitiveTableCell align='right' width={157}>
              <Tooltip
                content={formatMoney(transaction.accumulatedVolumeFxm)}
                disabled={transaction.accumulatedVolumeFxm < 100_000_000}
              >
                {formatMoney(transaction.accumulatedVolumeFxm)}
              </Tooltip>
            </SensitiveTableCell>
            <SensitiveTableCell align='right' width={150}>
              <Tooltip
                content={formatMoney(transaction.accumulatedVolumeOther)}
                disabled={transaction.accumulatedVolumeOther < 100_000_000}
              >
                {formatMoney(transaction.accumulatedVolumeOther)}
              </Tooltip>
            </SensitiveTableCell>
            <SensitiveTableCell align='right' width={150}>
              {formatMoney(transaction.accumulatedCommission)}
            </SensitiveTableCell>
            <SensitiveTableCell align='right' width={150}>
              {formatNumber(transaction.rebateAmount, { maxDecimalPlaces: 10 })}
            </SensitiveTableCell>
            <SensitiveTableCell>
              <Box flex align='center' gap='x8'>
                <StatusIcon styles={TRANSACTION_STATUS_STYLE} status={transaction.state.id} />
                <Box>
                  <SensitiveData>
                    <Body2 medium>
                      {transactionStatuses[transaction.state.id] ?? transaction.state.name}
                    </Body2>
                  </SensitiveData>
                </Box>
              </Box>
            </SensitiveTableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

interface EmptyTransactionsPageTableProps {
  isAnyFilterActive?: boolean
}

export const EmptyTransactionsPageTable: FC<EmptyTransactionsPageTableProps> = (props) => {
  const { isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={
        isAnyFilterActive
          ? t('IB.RebatesPage.Nothing to show')
          : t('IB.RebatesPage.TransactionsPage.No transactions to display')
      }
      content={
        isAnyFilterActive ? <Body1>{t('IB.RebatesPage.Please, adjust your filters')}</Body1> : null
      }
    />
  )
}
